/**
 * Keeps the session's access token fresh. Access tokens live five minutes (SEC-034: de-provisioning must bite
 * fast), and without renewal every user was dropped mid-work every five minutes: the next API call came back
 * 401 and the shell sent them to sign in again. The token is renewed at 75% of its lifetime with the refresh
 * token the provider issued; the provider rotates refresh tokens, so each renewal hands over a new one.
 *
 * The refresh token is held in memory only, never in browser storage: it outlives the access token, so it is
 * the more valuable thing to steal. After a reload it is gone, and the provider's own session signs the person
 * straight back in (one sign-in, no password while that session lasts). A refused renewal simply stops; the
 * next 401 ends the session the ordinary way, so a revoked or disabled user is never kept alive by this.
 */
export interface TokenSet {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
}

export interface RefreshDeps {
  /** Exchanges a refresh token for a new set; rejects when the provider refuses. */
  exchange: (refreshToken: string) => Promise<TokenSet>;
  /** Installs a renewed access token for the API client. */
  apply: (accessToken: string) => void;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  /** Told about every renewed set, so the caller can keep the new refresh token. */
  onRenewed?: (set: TokenSet) => void;
}

/** Renew this long before expiry; never sooner than the floor, so a tiny lifetime cannot spin. */
export const RENEW_AT_FRACTION = 0.75;
export const MIN_RENEW_MS = 10_000;

export function renewDelayMs(expiresInSeconds: number): number {
  return Math.max(MIN_RENEW_MS, Math.floor(expiresInSeconds * 1000 * RENEW_AT_FRACTION));
}

export function createSessionRefresher(deps: RefreshDeps) {
  let handle: unknown = null;
  let generation = 0;

  const stop = (): void => {
    generation++;
    if (handle !== null) deps.clearTimer(handle);
    handle = null;
  };

  const schedule = (set: TokenSet): void => {
    stop();
    if (!set.refreshToken) return;
    const mine = generation;
    const refreshToken = set.refreshToken;
    handle = deps.setTimer(() => {
      handle = null;
      deps
        .exchange(refreshToken)
        .then((next) => {
          if (mine !== generation) return; // signed out (or restarted) while the exchange was in flight
          deps.apply(next.accessToken);
          deps.onRenewed?.(next);
          schedule(next);
        })
        .catch(() => undefined); // refused: stop renewing; the next 401 ends the session normally
    }, renewDelayMs(set.expiresIn));
  };

  return { start: schedule, stop };
}
